import type { SimulationConfig } from './types.ts';
import { DEFAULT_SIM } from './library.ts';
import {
  parseDescription, describePendulum, listSaves, savePendulum, deleteSave,
  defaultSaveName, SYSTEM_BADGE,
} from './library.ts';

export interface LibraryUI {
  refresh(): void;
}

export function createLibrary(opts: {
  textarea: HTMLTextAreaElement;
  loadBtn: HTMLButtonElement;
  exportBtn: HTMLButtonElement;
  nameInput: HTMLInputElement;
  saveBtn: HTMLButtonElement;
  listEl: HTMLElement;
  statusEl: HTMLElement;
  getCurrentConfig: () => SimulationConfig | null;
  onLoad: (config: SimulationConfig) => void;
}): LibraryUI {
  const { textarea, loadBtn, exportBtn, nameInput, saveBtn, listEl, statusEl, getCurrentConfig, onLoad } = opts;

  let flashTimer: number | null = null;
  function flash(msg: string, isErr = false): void {
    statusEl.textContent = msg;
    statusEl.classList.toggle('err', isErr);
    if (flashTimer !== null) clearTimeout(flashTimer);
    if (msg) {
      flashTimer = window.setTimeout(() => {
        statusEl.textContent = '';
        statusEl.classList.remove('err');
        flashTimer = null;
      }, 3500);
    }
  }

  function renderList(): void {
    const saves = listSaves();
    listEl.innerHTML = '';
    if (saves.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'library-empty';
      empty.textContent = 'No saved pendulums yet.';
      listEl.appendChild(empty);
      return;
    }
    for (const s of saves) {
      const row = document.createElement('div');
      row.className = 'library-row';

      const item = document.createElement('button');
      item.className = 'library-item';
      item.type = 'button';
      item.title = 'Load this pendulum';
      const name = document.createElement('span');
      name.className = 'lib-name';
      name.textContent = s.name;
      const sys = document.createElement('span');
      sys.className = 'lib-sys';
      sys.textContent = SYSTEM_BADGE[s.system] ?? s.system;
      item.append(name, sys);
      item.addEventListener('click', () => {
        onLoad({
          system: s.system,
          params: { ...s.params },
          sim: { ...s.sim },
          initialState: { ...s.initialState },
        });
        flash(`Loaded “${s.name}”`);
      });

      const del = document.createElement('button');
      del.className = 'library-del';
      del.type = 'button';
      del.title = 'Delete';
      del.innerHTML = '&times;';
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        deleteSave(s.id);
        renderList();
        flash(`Deleted “${s.name}”`);
      });

      row.append(item, del);
      listEl.appendChild(row);
    }
  }

  loadBtn.addEventListener('click', () => {
    const text = textarea.value.trim();
    if (!text) { flash('Paste a pendulum description first.', true); return; }
    try {
      const desc = parseDescription(text);
      const cur = getCurrentConfig();
      const sim = cur ? { ...cur.sim } : { ...DEFAULT_SIM };
      // a description with no initial state keeps the current initial conditions
      const initialState = desc.initialState
        ? desc.initialState
        : (cur ? { ...cur.initialState } : { ...DEFAULT_INITIAL_STATE_FALLBACK });
      onLoad({ system: desc.system, params: desc.params, sim, initialState });
      flash(`Loaded ${SYSTEM_BADGE[desc.system] ?? desc.system} from JSON`);
    } catch (e) {
      flash((e as Error).message, true);
    }
  });

  exportBtn.addEventListener('click', () => {
    const cfg = getCurrentConfig();
    if (!cfg) { flash('Nothing to describe yet.', true); return; }
    textarea.value = describePendulum(cfg);
    flash('Wrote current pendulum as JSON');
  });

  function doSave(): void {
    const cfg = getCurrentConfig();
    if (!cfg) { flash('No configuration to save.', true); return; }
    const name = nameInput.value.trim() || defaultSaveName(cfg);
    const entry = savePendulum(name, cfg);
    if (!entry) { flash('Could not save (storage unavailable).', true); return; }
    nameInput.value = '';
    renderList();
    flash(`Saved “${entry.name}”`);
  }
  saveBtn.addEventListener('click', doSave);
  nameInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') doSave(); });

  renderList();
  return { refresh: renderList };
}

const DEFAULT_INITIAL_STATE_FALLBACK = {
  angle1: 1.5, velocity1: 0, stretch1: 0, stretchRate1: 0,
  angle2: 1.5, velocity2: 0, stretch2: 0, stretchRate2: 0,
};
